/* eslint-disable consistent-return */
import ContentfulImage from '@/components/contentful-image';
import YouTubeVideo from '@/components/videos/youtube-video';
import { CardIC } from '@/lib/interface-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import Link from 'next/link';
import ReactPlayer from 'react-player';
import Awards from '../awards/awards';
import NavigationButton from '../navigationButton';
import { Quote } from '../quote/Quote';
import RichText from '../rich-text';
import AccordionSection from '../section/accordion-section';
import ArticleSection from '../section/article-section';
import CardSection from '../section/card-section';

// Create a bespoke renderOptions object to target BLOCKS.EMBEDDED_ENTRY (linked block entries e.g. code blocks)
// INLINES.EMBEDDED_ENTRY (linked inline entries e.g. a reference to another blog post)
// and BLOCKS.EMBEDDED_ASSET (linked assets e.g. images)

function renderOptions(links: any, isLandingType?: boolean) {
  // create an asset map
  const assetMap = new Map();
  // loop through the assets and add them to the map

  for (const asset of links.assets.block) {
    assetMap.set(asset.sys.id, asset);
  }

  // create an entry map
  const entryMap = new Map();
  // loop through the block linked entries and add them to the map

  for (const entry of links.entries.block) {
    entryMap.set(entry.sys.id, entry);
  }

  // loop through the inline linked entries and add them to the map

  //   for (const entry of links.entries.inline) {
  //     entryMap.set(entry.sys.id, entry);
  //   }

  return {
    renderNode: {
      [INLINES.EMBEDDED_ENTRY]: (node: any, children: any) => {
        // find the entry in the entryMap by ID
        // eslint-disable-next-line react/destructuring-assignment
        const entry = entryMap.get(node.data.target.sys.id);

        // render the entries as needed
        // eslint-disable-next-line no-underscore-dangle
        if (entry?.__typename === 'BlogPost') {
          return <a href={`/blog/${entry.slug}`}>{entry.title}</a>;
        }
      },

      [INLINES.HYPERLINK]: (node: any, children: any) => {
        return <Link href={node.data.uri}>{children}</Link>;
      },

      // [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      //   const { nodeType, content } = node;
      //   if (nodeType === 'paragraph') {
      //     const text = content[0].value.trim();
      //     const words = text.split(' ');
      //     const shouldTruncate = words.length > 50;
      //     const truncatedText = `${words.slice(0, 50).join(' ')} `;
      //     const [showFullText, setShowFullText] = useState(false);

      //     const handleClick = () => {
      //       setShowFullText(!showFullText);
      //     };

      //     return (
      //       <p className="max-w-full 2xl:max-w-[80%]">
      //         {shouldTruncate && !showFullText ? (
      //           <p>
      //             {truncatedText}
      //             <span>&nbsp;</span>
      //             <span
      //               onClick={handleClick}
      //               className="cursor-pointer underline"
      //               role="presentation"
      //             >
      //               Read more
      //             </span>
      //           </p>
      //         ) : (
      //           <p className="max-w-full 2xl:max-w-[80%]">
      //             {text} <span>&nbsp;</span>
      //             {shouldTruncate && showFullText && (
      //               <span
      //                 className="cursor-pointer"
      //                 onClick={handleClick}
      //                 role="presentation"
      //               >
      //                 <span className="cursor-pointer underline">
      //                   Read less
      //                 </span>
      //               </span>
      //             )}
      //           </p>
      //         )}
      //       </p>
      //     );
      //   }
      // },
      [BLOCKS.QUOTE]: (node: any, children: any) => {
        const { nodeType, content } = node;
        const text = content[0].content[0].value;
        // console.log('content[0].content[0].value', content);
        return <p>{`"${text}"`}</p>;
      },

      [BLOCKS.EMBEDDED_ENTRY]: (node: any, children: any) => {
        // find the entry in the entryMap by ID
        // eslint-disable-next-line react/destructuring-assignment
        const entry = entryMap.get(node.data.target.sys.id);

        // render the entries as needed by looking at the __typename
        // referenced in the GraphQL query
        if (entry.__typename === 'PicturesInRow') {
          const { items } = entry.imagesCollection;
          return (
            items && (
              <div className="PicturesInRow grid xl:grid-cols-2 gap-10 my-16">
                {items.map((e: any) => {
                  return (
                    <div
                      className="relative h-[330px] 2xl:h-[450px] w-full"
                      key={e.url}
                    >
                      {e?.url && (
                        <ContentfulImage
                          src={e.url}
                          fill
                          className="object-cover"
                          sizes="99.99vw"
                          alt={e.title}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )
          );
        }

        if (entry.__typename === 'PictureAndTextInRow') {
          const {
            picturePosition,
            titleRow,
            description,
            pictureAndTextInRowContent
          } = entry;
          const buttonContent =
            pictureAndTextInRowContent?.links?.entries?.block;
          const { url, title } = entry.picture || {};
          return (
            <div>
              {titleRow && !isLandingType && (
                <h2 className="mt-12 lg:mt-20">{titleRow}</h2>
              )}
              <div
                className={`PictureAndTextInRow grid sm:grid-cols-2 gap-12 my-10 ${
                  isLandingType ? 'xl:my-20' : 'xl:my-20'
                }`}
              >
                <div
                  className={`relative h-[330px] 2xl:h-[450px] ${
                    picturePosition === true ? 'order-1' : 'order-2'
                  }`}
                >
                  <ContentfulImage
                    src={url}
                    fill
                    className="object-cover"
                    sizes="99.99vw"
                    alt={title}
                  />
                </div>
                <div
                  className={` ${
                    picturePosition === true ? 'order-2' : 'order-1'
                  }`}
                >
                  <div>
                    {titleRow && isLandingType && (
                      <h2 className="mb-6">{titleRow}</h2>
                    )}
                    {description && <RichText data={description} />}
                    {pictureAndTextInRowContent && (
                      <div className="CustomButton mt-10">
                        <NavigationButton data={buttonContent[0]} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }
        if (entry.__typename === 'CardSection') {
          const { title, sectionContentCollection, layout } = entry;
          return layout.includes('Accordion') ? (
            <AccordionSection
              title={title}
              items={sectionContentCollection.items.filter(
                (item: CardIC) => item.__typename === 'Accordion'
              )}
              item={sectionContentCollection.items.find(
                (item: CardIC) => item.__typename === 'Card'
              )}
              layout={layout}
            />
          ) : (
            <CardSection section={entry} slug={''} />
          );
        }

        if (entry.__typename === 'Card') {
          return (
            <div className="my-12 lg:my-20">
              <YouTubeVideo
                data={entry.videoUrl}
                videoHeight="h-[330px] lg:h-[600px]"
              />
            </div>
          );
        }

        if (entry.__typename === 'Quote') {
          return (
            <div>
              <Quote section={entry} />
            </div>
          );
        }

        // eslint-disable-next-line no-underscore-dangle
        if (entry.__typename === 'VideoEmbed') {
          return (
            <iframe
              src={entry.embedUrl}
              height="100%"
              width="100%"
              frameBorder="0"
              scrolling="no"
              title={entry.title}
              allowFullScreen
            />
          );
        }
        if (entry.__typename === 'YouTubeVideo') {
          return (
            <div className="my-12 lg:my-20">
              <YouTubeVideo
                data={entry.videoUrl}
                videoHeight="h-[330px] lg:h-[500px]"
              />
            </div>
          );
        }
        if (entry.__typename === 'NavigationButton') {
          return (
            <div className="CustomButton mt-20">
              <NavigationButton data={entry} />
            </div>
          );
        }
        if (entry.__typename === 'AwardsAndCertificates') {
          return <Awards section={entry} />;
        }
        if (entry.__typename === 'ArticleSection') {
          return <ArticleSection section={entry} />;
        }
        throw new Error(`Unknown entry type name!${entry.__typename}`);
      },
      [BLOCKS.EMBEDDED_ASSET]: (node: any, next: any) => {
        // find the asset in the assetMap by ID
        // eslint-disable-next-line react/destructuring-assignment
        const asset = assetMap.get(node.data.target.sys.id);

        const isVideoAsset = asset.url.startsWith('https://videos');
        const isImageAsset = asset.url.startsWith('https://images');

        return (
          <div className="relative mt-12 mb-12 lg:mt-20 lg:mb-20 h-[330px] xl:h-[500px] 2xl:h-[600px] w-full">
            {isImageAsset && (
              <ContentfulImage
                fill
                src={asset.url}
                sizes="99.99vw"
                className="object-cover"
              />
            )}
            {isVideoAsset && (
              <ReactPlayer
                width="100%"
                height="100%"
                url={asset.url}
                playing
                muted
                controls={false}
                loop
              />
            )}
          </div>
        );
      }
    }
  };
}

// Render post.body.json to the DOM using
// documentToReactComponents from "@contentful/rich-text-react-renderer"

const RichTextArticle = (props: any) => {
  const { data, isLandingType } = props;

  return (
    <>
      {documentToReactComponents(
        data.json,
        renderOptions(data.links, isLandingType)
      )}
    </>
  );
};

export default RichTextArticle;
